import React from 'react';
import { Button, mediaQueries, style } from '@do/walrus';
import iconVariantACommunity from '../assets/icon-variant-a-community.svg';
import iconVariantAScalability from '../assets/icon-variant-a-scalability.svg';
import iconVariantBBudget from '../assets/icon-variant-b-budget.svg';
import iconVariantBReliability from '../assets/icon-variant-b-reliability.svg';
import styled from 'styled-components';
import { Column } from './Common';
import { Sizes } from '../interfaces/loginVariant';
import { GROW_3829_SIGNUP_SELLING_POINTS_TREATMENT_B } from '../constants';

interface Props {
  step: number;
  variant?: string;
}

interface VariantCopyProps {
  color?: string;
}

const StyledButton = styled(Button)`
  border-radius: 29.467px;
  background: ${style.colors.white};
  padding: 0.4375rem 1.375rem;
  border: none;

  &:hover {
    background: ${style.colors.white};
    text-decoration: none;
  }

  @media (max-width: 1024px) {
    margin-bottom: 5rem;
  }
`;

const ImgWrapper = styled.img<{ width?: string; margin?: string }>`
  width: ${({ width }) => width || '100%'};
  margin: ${({ margin }) => margin || '0'};
`;

const titleImageSizes: Record<Sizes, string> = {
  xlarge: '400px',
  large: '300px',
  medium: '250px',
  small: '80px',
};

const Title = styled.h2<VariantCopyProps>`
  && {
    white-space: pre-line;
    padding: 0;
    margin: 0;
    color: ${({ color }) => (color ? color : 'initial')};
    font-size: 1.5rem;
    ${mediaQueries.mediumMax} {
      font-size: 1.625rem;
    }
  }
`;

const SubTitle = styled.h4<VariantCopyProps>`
  && {
    white-space: pre-line;
    padding: 0;
    margin-top: 0;
    color: ${({ color }) => (color ? color : 'initial')};
    font-size: 1rem;
    ${mediaQueries.mediumMin} {
      font-size: 1.375rem;
      font-weight: 500;
    }
  }
`;

const SellingPointsColumn = styled(Column)`
  display: flex;
  flex-direction: column;
  max-width: 45.625rem;

  padding: 2rem 0.5rem 0;

  @media (min-width: 426px) {
    padding: 2rem 1.5rem 0;
  }

  @media (min-width: 768px) {
    padding-top: 13rem;
    padding-left: 3.125rem;
    padding-right: 2.5rem;
  }

  @media (min-width: 1025px) {
    padding-top: 7rem;
    padding-right: 4.125rem;
  }

  @media (min-width: 1440px) {
    padding-right: 8.125rem;
  }
`;

const SellingPointsContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${style.vars.space['3']};
`;

const SellingPoints = ({ step, variant }: Props) => {
  const isStepZero = step === 0;
  const titleColor = 'var(--Primary-Neutral-White, #fff)';

  const isVariantB = variant === GROW_3829_SIGNUP_SELLING_POINTS_TREATMENT_B;
  const showCTA = !isVariantB;

  const titleText = isVariantB
    ? isStepZero
      ? 'Use your budget on builds, not bills'
      : 'A reliable platform that scales with you'
    : isStepZero
    ? 'Find inspiration with our community'
    : 'Stability that scales with you';

  const subtitleText = isVariantB
    ? isStepZero
      ? '“We were previously on AWS but started migrating everything because we really love DigitalOcean in terms of cost and support. We saved a lot of money—we went from $120,000 to $30,000 [per month]” – Andres Murcia, CTO, Picap'
      : '“Thanks to all of these improvements with fantastic support from DigitalOcean we’ve earned robust customer trust through our consistently maintained 99.99% uptime...” – Vijay Dharmaraj, Chief Architect & Security Officer at FieldEx'
    : isStepZero
    ? 'Join our community of over half a million developers and discover ways to learn, grow, and contribute. With hosted Q&As, our annual Hacktoberfest, and over 3,000 comprehensive guides, there’s always something new to explore.'
    : 'With approximately two million droplets spun up today across 171 countries, DigitalOcean provides the stability you need to create a reliable and scalable solution.';

  const iconSrc = isVariantB
    ? isStepZero
      ? iconVariantBBudget
      : iconVariantBReliability
    : isStepZero
    ? iconVariantACommunity
    : iconVariantAScalability;

  const buttonUrl = isStepZero ? 'https://www.digitalocean.com/community' : 'https://marketplace.digitalocean.com/';

  const buttonText = isStepZero ? 'Go to community' : 'Learn more';

  return (
    <SellingPointsColumn>
      <SellingPointsContent>
        <ImgWrapper src={iconSrc} width={titleImageSizes.small} alt="Selling points icon" />

        <Title color={titleColor}>{titleText}</Title>

        <SubTitle color={titleColor}>{subtitleText}</SubTitle>

        {showCTA && (
          <StyledButton variation="banner-secondary" url={buttonUrl} external rel="noopener noreferrer">
            {buttonText}
          </StyledButton>
        )}
      </SellingPointsContent>
    </SellingPointsColumn>
  );
};

export default SellingPoints;
